import { ComponentProps, FocusEvent, ReactNode, useId, useState } from 'react';

import { Input as InputUI } from '@/components/ui/input';
import { Label as LabelUI } from '@/components/ui/label';

import { cn } from '@/lib/utils';

type InputProps = Omit<ComponentProps<'input'>, 'placeholder'> & {
  label?: ReactNode;
  icon?: ReactNode;
  isInvalid?: boolean;
  isActive?: boolean;
  shouldFloatLabel?: boolean;
};

const Input = (props: InputProps) => {
  const {
    className,
    label,
    icon,
    id,
    onFocus,
    onBlur,
    value,
    defaultValue,
    isInvalid,
    isActive,
    shouldFloatLabel,
    ...restProps
  } = props;

  const generatedId = useId();
  const inputId = id ?? generatedId;

  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(() => {
    return Boolean(value ?? defaultValue);
  });

  const resolvedHasValue = value !== undefined ? Boolean(value) : hasValue;
  const showFocused = isActive ?? isFocused;
  const isFloating =
    showFocused || resolvedHasValue || Boolean(shouldFloatLabel);

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    setHasValue(Boolean(e.target.value));
    onBlur?.(e);
  };

  return (
    <div className="relative">
      {icon && (
        <span className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 [&>svg]:size-4">
          {icon}
        </span>
      )}
      <InputUI
        id={inputId}
        value={value}
        defaultValue={defaultValue}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={cn(
          'dark:bg-card h-10 border-transparent bg-transparent placeholder:text-transparent',
          'focus-visible:border-transparent',
          'aria-invalid:border-transparent',
          icon && 'pl-9',
          className,
        )}
        {...restProps}
      />
      {label && (
        <>
          <fieldset
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-0 top-[-5px] overflow-hidden rounded-md bg-transparent px-[9px] transition-colors',
              showFocused && !isInvalid && 'border-primary border-2',
              !showFocused && !isInvalid && 'border-input border-2',
              isInvalid && 'border-destructive border-2',
            )}
          >
            <legend
              className={cn(
                'invisible block h-[11px] w-auto overflow-hidden p-0 text-xs whitespace-nowrap',
                isFloating
                  ? 'max-w-full transition-[max-width] delay-50 duration-100 ease-out'
                  : 'max-w-[0.01px] transition-[max-width] duration-50 ease-out',
              )}
            >
              <span className="visible inline-block px-1 opacity-0">
                {label}
              </span>
            </legend>
          </fieldset>
          <LabelUI
            htmlFor={inputId}
            className={cn(
              'text-muted-foreground pointer-events-none absolute gap-1 transition-all duration-200 ease-out',
              isFloating
                ? 'top-0 left-[13px] -translate-y-1/2 text-xs'
                : cn(
                    'top-1/2 -translate-y-1/2 text-sm',
                    icon ? 'left-9' : 'left-[13px]',
                  ),
              isInvalid ? 'text-destructive' : showFocused && 'text-primary',
            )}
          >
            {label}
          </LabelUI>
        </>
      )}
    </div>
  );
};

export { Input };
export type { InputProps };
